import { useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { FileText, AlertCircle, Copy, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_NOTES = `AGM held Thursday 6pm at building common room.
Present: Sarah Chen (chair), Marcus Wong (treasurer), Priya Patel, James O'Brien (lot 12).
Apologies from Tom Davies (lot 4).
Sarah confirmed quorum reached.
Last AGM minutes tabled — confirmed by all present.
Motion 1: approve 2026 budget of $84,200 including 6% admin fund increase. Moved Marcus, seconded Priya. Carried unanimously.
Motion 2: engage Bright Strata for AGM admin support next year, $2,400/yr. Moved James, seconded Sarah. Carried 3-1, James abstained from final vote on cost.
Discussion on roof leak — Marcus to get 3 quotes by 28 Feb.
Action: Sarah to update bylaw register.
Next meeting: Wed 12 March 7pm same place.`;

import { streamSSE } from "@/lib/sseStream";

export default function MinutesDrafter() {
  const [state, setState] = useState("NSW");
  const [meetingType, setMeetingType] = useState("AGM");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [liveDraft, setLiveDraft] = useState("");
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (notes.trim().length < 10) {
      toast.error("Please add some chair notes first (10+ characters).");
      return;
    }
    setLoading(true); setStreaming(true); setError(null); setResult(null); setLiveDraft("");
    await streamSSE({
      url: `${API}/ai/minutes/draft`,
      body: { scheme_state: state, meeting_type: meetingType, chair_notes: notes },
      onEvent: ({ event, data }) => {
        if (event === "token") {
          setLoading(false);
          setLiveDraft((prev) => prev + (data?.text || ""));
        } else if (event === "done") {
          setResult(data);
          setStreaming(false); setLoading(false);
        } else if (event === "error") {
          setError(data?.message || "AI provider error");
          toast.error("Could not generate minutes draft.");
          setStreaming(false); setLoading(false);
        }
      },
      onError: (err) => {
        const msg = err?.message || "Failed to generate draft.";
        setError(msg);
        toast.error("Could not generate minutes draft.");
        setStreaming(false); setLoading(false);
      },
    });
  };

  const copyOutput = async () => {
    if (!result?.draft_minutes) return;
    await navigator.clipboard.writeText(result.draft_minutes);
    setCopied(true);
    toast.success("Draft copied to clipboard.");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Card
      className="border rounded-xl shadow-sm bg-white"
      style={{ borderColor: "var(--border)" }}
      data-testid="minutes-drafter-card"
    >
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{
              width: 32,
              height: 32,
              background: "var(--blue-light)",
              color: "var(--blue-dark)",
            }}
          >
            <FileText size={16} strokeWidth={2.25} />
          </div>
          <div>
            <CardTitle
              className="text-base font-bold"
              style={{ color: "var(--navy)" }}
            >
              AI Minutes Drafter
            </CardTitle>
            <CardDescription
              className="text-xs"
              style={{ color: "var(--slate)" }}
            >
              Turn chair notes into a clean, audit-ready draft.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label
              htmlFor="state-md"
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: "var(--slate)", letterSpacing: "0.08em" }}
            >
              Scheme state
            </Label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger
                id="state-md"
                className="h-10"
                data-testid="minutes-state-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NSW" data-testid="minutes-state-NSW">
                  NSW — Strata Schemes Management Act 2015
                </SelectItem>
                <SelectItem value="QLD" data-testid="minutes-state-QLD">
                  QLD — BCCM Act 1997
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label
              htmlFor="meeting-type"
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: "var(--slate)", letterSpacing: "0.08em" }}
            >
              Meeting type
            </Label>
            <Select value={meetingType} onValueChange={setMeetingType}>
              <SelectTrigger
                id="meeting-type"
                className="h-10"
                data-testid="minutes-meeting-type-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AGM" data-testid="meeting-type-AGM">
                  AGM
                </SelectItem>
                <SelectItem value="EGM" data-testid="meeting-type-EGM">
                  EGM
                </SelectItem>
                <SelectItem value="Committee" data-testid="meeting-type-Committee">
                  Committee
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="chair-notes"
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: "var(--slate)", letterSpacing: "0.08em" }}
            >
              Chair notes
            </Label>
            <button
              type="button"
              className="text-[11px] font-medium hover:underline"
              style={{ color: "var(--blue)" }}
              onClick={() => setNotes(SAMPLE_NOTES)}
              data-testid="minutes-load-sample-btn"
            >
              Load sample
            </button>
          </div>
          <Textarea
            id="chair-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Paste raw chair notes — attendees, motions, votes, action items, anything discussed…"
            className="min-h-[180px] resize-y leading-relaxed"
            style={{ fontSize: "0.9rem" }}
            data-testid="minutes-notes-textarea"
          />
        </div>

        <Button
          onClick={generate}
          disabled={loading}
          className="w-full h-11 font-semibold"
          style={{
            background: "var(--blue)",
            color: "white",
          }}
          data-testid="minutes-generate-btn"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              Drafting
              <span className="inline-flex gap-1 ml-1">
                <span className="ai-dot" />
                <span className="ai-dot" />
                <span className="ai-dot" />
              </span>
            </span>
          ) : (
            "Generate draft"
          )}
        </Button>

        {error && (
          <div
            className="flex items-start gap-2 rounded-lg p-3 text-sm"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="minutes-error"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {streaming && liveDraft && !result && (
          <div
            className="rounded-lg border bg-white p-4"
            style={{ borderColor: "var(--blue)" }}
            data-testid="minutes-live-draft"
          >
            <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--blue)" }}>
              <span className="ai-dot" data-testid="minutes-streaming-indicator"/> Streaming…
            </div>
            <pre
              className="whitespace-pre-wrap text-sm font-sans"
              style={{ color: "var(--navy)" }}
              data-testid="minutes-live-text"
            >{liveDraft}</pre>
          </div>
        )}

        {result && (
          <div
            className="rounded-lg border bg-white"
            style={{ borderColor: "var(--border)" }}
            data-testid="minutes-result"
          >
            <div
              className="flex items-center justify-between px-4 py-2.5 border-b"
              style={{ borderColor: "var(--border)" }}
            >
              <span className="draft-badge" data-testid="minutes-draft-badge">
                ● Draft — review before publishing
              </span>
              <button
                type="button"
                onClick={copyOutput}
                className="inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
                style={{ color: "var(--slate)" }}
                data-testid="minutes-copy-btn"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="px-5 py-4 markdown-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {result.draft_minutes}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </CardContent>

      {result && (
        <CardFooter
          className="block border-t pt-3"
          style={{ borderColor: "var(--border)" }}
        >
          <p
            className="text-[11px] italic leading-snug"
            style={{ color: "var(--tagline-grey)" }}
            data-testid="minutes-disclaimer"
          >
            {result.disclaimer}
          </p>
        </CardFooter>
      )}
    </Card>
  );
}
